import { type ClientConfig, createClient } from '@sanity/client';
import createImageUrlBuilder from '@sanity/image-url';
import type {
  ImageUrlBuilderOptions,
  SanityImageSource,
} from '@sanity/image-url/lib/types/types';

/**
 * NOTE: The CDN client may return stale data.
 * For all static builds (including revalidation builds), the live (non-CDN) client should be used.
 *
 * See https://www.sanity.io/docs/api-cdn
 */

const BASE_CONFIG: ClientConfig = {
  projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID ?? '',
  dataset: process.env.NEXT_PUBLIC_SANITY_DATASET ?? 'staging', // default to staging for local builds
  apiVersion: 'v2022-08-08',
};

const CDN_CONFIG: ClientConfig = {
  ...BASE_CONFIG,
  useCdn: true,
};

export const sanityClientWithCdn = createClient(CDN_CONFIG);

export const sanityClient = createClient({ ...BASE_CONFIG, useCdn: false });

interface SanityImageUrlOptions extends Partial<ImageUrlBuilderOptions> {
  image: SanityImageSource;
}

let _sanityImageBuilder: ReturnType<typeof createImageUrlBuilder>;
export const getSanityImageUrl = ({
  image,
  ...options
}: SanityImageUrlOptions) => {
  if (!_sanityImageBuilder) {
    _sanityImageBuilder = createImageUrlBuilder(sanityClientWithCdn);
  }

  return _sanityImageBuilder.image(image).withOptions(options).url();
};
